import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

interface Publication {
  title: string;
  url: string;
}

interface Category {
  name: string;
  publications: Publication[];
}

const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
} as const;

const categories: Category[] = [
  {
    name: "Fully Closed Loop Studies for Open Source Automated Insulin Systems",
    publications: [
      {
        title:
          "First Use of Open-Source Automated Insulin Delivery AndroidAPS in Full Closed-Loop Scenario: Pancreas4ALL Randomized Pilot Study",
        url: "https://www.liebertpub.com/doi/abs/10.1089/dia.2022.0562",
      },
    ],
  },
  {
    name: "Safety Studies",
    publications: [
      {
        title:
          "In Silico Trials of an Open-Source Android-Based Artificial Pancreas: A New Paradigm to Test Safety and Efficacy of Do-It-Yourself Systems",
        url: "https://www.liebertpub.com/doi/abs/10.1089/dia.2019.0375",
      },
      {
        title:
          "Real-world efficacy and safety of open-source automated insulin delivery for people with type 1 diabetes mellitus: Experience from mainland China",
        url: "https://www.sciencedirect.com/science/article/abs/pii/S0168822724008209",
      },
    ],
  },
  {
    name: "Observational Studies",
    publications: [
      {
        title:
          "Large-Scale Data Analysis for Glucose Variability Outcomes with Open-Source Automated Insulin Delivery Systems",
        url: "https://www.mdpi.com/2072-6643/14/9/1906",
      },
      {
        title:
          "Why #WeAreNotWaiting—Motivations and Self-Reported Outcomes Among Users of Open-source Automated Insulin Delivery Systems: Multinational Survey",
        url: "https://www.jmir.org/2021/6/e25409/",
      },
    ],
  },
  {
    name: "Pregnancy",
    publications: [
      {
        title:
          "Open-source automated insulin delivery systems for the management of type 1 diabetes during pregnancy",
        url: "https://casereports.bmj.com/content/14/9/e243522.abstract",
      },
    ],
  },
  {
    name: "Qualitative Studies",
    publications: [
      {
        title:
          "A Real-World Prospective Study of the Safety and Effectiveness of the Loop Open Source Automated Insulin Delivery System",
        url: "https://www.liebertpub.com/doi/full/10.1089/dia.2020.0535",
      },
      {
        title:
          "Open source automated insulin delivery: addressing the challenge",
        url: "https://www.nature.com/articles/s41746-019-0202-1",
      },
      {
        title:
          "Open-source automated insulin delivery systems and formal healthcare: A qualitative study of challenges in the interaction between service-users with type 1 diabetes and healthcare professionals",
        url: "https://journals.sagepub.com/doi/full/10.1177/17423953221142341",
      },
      {
        title:
          "Barriers to uptake of Open-Source automated insulin delivery Systems: Analysis of socioeconomic factors and perceived challenges of adults with type 1 diabetes from the OPEN survey",
        url: "https://www.sciencedirect.com/science/article/abs/pii/S016882272201049X",
      },
      {
        title:
          "Qualitative Study of User Experiences with Loop, an Open-Source Automated Insulin Delivery System",
        url: "https://www.liebertpub.com/doi/abs/10.1089/dia.2021.0485",
      },
    ],
  },
  {
    name: "Comparative Studies (RCTs and Others)",
    publications: [
      {
        title: "Open-Source Automated Insulin Delivery in Type 1 Diabetes",
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa2203913",
      },
      {
        title:
          "Extended Use of an Open-Source Automated Insulin Delivery System in Children and Adults with Type 1 Diabetes: The 24-Week Continuation Phase Following the CREATE Randomized Controlled Trial",
        url: "https://www.liebertpub.com/doi/abs/10.1089/dia.2022.0484",
      },
      {
        title:
          "CREATE (Community deRivEd AutomaTEd insulin delivery) trial. Randomised parallel arm open label clinical trial comparing automated insulin delivery using a mobile controller (AnyDANA-loop) with an open-source algorithm with sensor augmented pump therapy in type 1 diabetes",
        url: "https://link.springer.com/article/10.1007/s40200-020-00547-8",
      },
      {
        title:
          "Open-Source Versus Commercial Automated Insulin Delivery System for Type 1 Diabetes Management: A Prospective Observational Comparative Study from Canada",
        url: "https://www.liebertpub.com/doi/abs/10.1089/dia.2024.0561",
      },
    ],
  },
  {
    name: "Systematic Reviews",
    publications: [
      {
        title:
          "Do-it-yourself artificial pancreas systems: a review of the emerging evidence and insights for healthcare professionals",
        url: "https://journals.sagepub.com/doi/full/10.1177/1932296819894296",
      },
      {
        title: "The do-it-yourself artificial pancreas: a comprehensive review",
        url: "https://link.springer.com/article/10.1007/s13300-020-00823-z",
      },
      {
        title:
          "Efficacy, safety, and user experience of DIY or open-source artificial pancreas systems: a systematic review",
        url: "https://link.springer.com/article/10.1007/s00592-020-01623-4",
      },
      {
        title:
          "A scoping review of Do-It-Yourself Automated Insulin Delivery system (DIY AID) use in people with type 1 diabetes",
        url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0271096",
      },
    ],
  },
  {
    name: "Sources for Healthcare Providers",
    publications: [
      {
        title:
          "Open-source automated insulin delivery: international consensus statement and practical guidance for health-care professionals",
        url: "https://www.thelancet.com/journals/landia/article/PIIS2213-8587(21)00267-9/abstract",
      },
      {
        title:
          "Practical Guidance on Open Source and Commercial Automated Insulin Delivery Systems: A Guide for Healthcare Professionals Supporting People with Insulin-Requiring Diabetes",
        url: "https://link.springer.com/article/10.1007/s13300-022-01299-9",
      },
    ],
  },
];

const Page = () => {
  const { t } = useTranslation();
  usePageView();

  return (
    <>
      <Seo
        title="Scientific Publications"
        description="Publications about open-source automated insulin delivery systems."
      />
      <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pt: { xs: 10, md: 14 },
            pb: { xs: 3, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "primary.main",
                  display: "block",
                  mb: 1.5,
                }}
              >
                Resources
              </Typography>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  fontSize: { xs: "2.4rem", md: "3.5rem" },
                }}
              >
                {t("Scientific Publications")}
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Stack spacing={4} sx={{ maxWidth: 760 }}>
            <Box
              sx={{
                borderLeft: 3,
                borderColor: "primary.main",
                pl: 2.5,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={bodySx}>
                {t(
                  "Publications about open-source automated insulin delivery systems."
                )}
              </Typography>
            </Box>

            {categories.map((category) => (
              <Stack key={category.name} spacing={2}>
                <Typography
                  component="h2"
                  variant="h5"
                  sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}
                >
                  {t(category.name)}
                </Typography>
                {category.publications.map((publication) => (
                  <Link
                    key={publication.url}
                    href={publication.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      display: "block",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 2,
                      color: "text.primary",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    {publication.title}
                  </Link>
                ))}
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
